import { formatInstant, unixToDate } from '@lib/datetime'
import { chatDayKey, chatDayTitle } from './dayTitle'
import { reactionEmoji } from './limits'
import { presentQuote, type ChatQuotePresentation } from './quotePresentation'
import type { ChatStream } from './stream'
import type {
	ChatAuthor,
	ChatCard,
	ChatLinkPreview,
	ChatMention,
	ChatMessage,
	ChatPhoto,
	ChatReactionCount,
} from './types'

export const REMOVED_MESSAGE_TEXT = 'Message deleted'
export const UNREAD_DIVIDER_ID = 'unread'

export type ChatDelivery = { status: 'sent'; messageId: number } | { status: 'sending' } | { status: 'failed' }

/** A Message as its bubble shows it. */
export interface ChatMessageRow {
	clientId: string
	/** "Message deleted" for a deleted Message; empty for one of cards or photos alone. */
	text: string
	isRemoved: boolean
	/** Mine sit on the right, without a name or avatar. */
	isMine: boolean
	delivery: ChatDelivery
	/** When it was sent, on the viewer's clock: "9:41 AM" or "09:41". */
	time: string
	/** Where tagged members' `@Name`s are in `text`, to highlight, in order. */
	mentions: ChatMention[]
	/**
	 * The resources it tags itself, as cards under the text. Members show inline instead,
	 * and what a reply inherited from its quote doesn't show.
	 */
	cards: ChatCard[]
	/** What a reply quotes, over its text. */
	quote: ChatQuotePresentation | null
	/** Others' Messages name their author on the first of a run from them… */
	authorName: string | null
	/** …and show their avatar beside the last. */
	avatar: ChatAuthor | null
	/** A grid in the bubble, in the order sent. */
	photos: ChatPhoto[]
	/** The preview of its first link, over the text; null once the link's Idea card stands in for it. */
	linkPreview: ChatLinkPreview | null
	/** Add as idea is on its way to the server. */
	isAddingIdea: boolean
	/** As chips under the bubble, in the order the six show. */
	reactions: ChatReactionCount[]
}

/** One row of a stream, as the screen shows it. `id` keeps the row's identity across renders. */
export type ChatStreamItem =
	| { type: 'day'; id: string; title: string }
	| { type: 'unreadDivider'; id: string; title: string }
	| { type: 'message'; id: string; row: ChatMessageRow }

/** The preview offers "+ Add as idea" until the link is an Idea, or is on its way to being one. */
export const canAddAsIdea = (row: ChatMessageRow): boolean =>
	row.linkPreview !== null && row.linkPreview.ideaId === null && !row.isAddingIdea

/** The server's id, once it has the Message. */
export const sentMessageId = (row: ChatMessageRow): number | null =>
	row.delivery.status === 'sent' ? row.delivery.messageId : null

/** Where the server says the tagged members' `@Name`s sit, in order. */
const mentionsOf = (cards: ChatCard[]): ChatMention[] =>
	cards
		.filter(card => card.kind === 'Member')
		.flatMap(card => card.mentions)
		.sort((a, b) => a.offset - b.offset)

/** A link added as an Idea shows as the Idea's card, which takes the preview's place. */
const shownPreview = (message: ChatMessage, ownCards: ChatCard[]): ChatLinkPreview | null => {
	const preview = message.linkPreview
	if (!preview) return null
	const ideaCardShows = preview.ideaId !== null && ownCards.some(c => c.kind === 'Idea' && c.id === preview.ideaId)
	return ideaCardShows ? null : preview
}

const emptyRowParts = { authorName: null, avatar: null, linkPreview: null, isAddingIdea: false, reactions: [] }

/**
 * Lays a stream out as the rows the screen shows: a separator opening each day, the
 * "N new" divider at the first Message I haven't read, and the Messages, confirmed ones
 * first and then the ones still sending. Ported from iOS `ChatStreamLayout`.
 */
export const layoutStream = (stream: ChatStream, myUserId: number, now: Date = new Date()): ChatStreamItem[] => {
	const items: ChatStreamItem[] = []
	let currentDay: string | null = null

	const openDay = (date: Date) => {
		const key = chatDayKey(date)
		if (key === currentDay) return
		currentDay = key
		items.push({ type: 'day', id: `day-${key}`, title: chatDayTitle(date, now) })
	}

	const marker = stream.unreadMarker
	const firstUnreadId = marker
		? (stream.messages.find(
				m => m.author.id !== myUserId && !m.removed && m.id > (marker.afterMessageId ?? -Infinity),
			)?.id ?? null)
		: null

	for (const message of stream.messages) {
		const date = unixToDate(message.timestamp)
		openDay(date)
		if (marker && message.id === firstUnreadId) {
			// The divider names the day, so it stands in for a separator it follows.
			if (items[items.length - 1]?.type === 'day') items.pop()
			items.push({
				type: 'unreadDivider',
				id: UNREAD_DIVIDER_ID,
				title: `${marker.count} new · ${chatDayTitle(date, now)}`,
			})
		}
		const ownCards = message.tags.filter(card => !card.inherited)
		items.push({
			type: 'message',
			id: `message-${message.clientId}`,
			row: {
				...emptyRowParts,
				clientId: message.clientId,
				text: message.removed ? REMOVED_MESSAGE_TEXT : (message.text ?? ''),
				isRemoved: message.removed,
				isMine: message.author.id === myUserId,
				delivery: { status: 'sent', messageId: message.id },
				time: formatInstant(date, 'time'),
				mentions: message.removed ? [] : mentionsOf(ownCards),
				cards: ownCards.filter(card => card.kind !== 'Member'),
				quote: message.quote ? presentQuote(message.quote, myUserId) : null,
				photos: message.photos,
				linkPreview: shownPreview(message, ownCards),
				isAddingIdea: stream.addingIdeas.includes(message.id),
				reactions: message.reactions.filter(r => reactionEmoji(r.reaction) !== null),
			},
		})
	}

	for (const pending of stream.pending) {
		const date = unixToDate(pending.date)
		openDay(date)
		const { message } = pending
		items.push({
			type: 'message',
			id: `message-${pending.clientId}`,
			row: {
				...emptyRowParts,
				clientId: pending.clientId,
				text: message.text ?? '',
				isRemoved: false,
				isMine: true,
				delivery: pending.status === 'failed' ? { status: 'failed' } : { status: 'sending' },
				time: formatInstant(date, 'time'),
				mentions: mentionsOf(message.cards),
				cards: message.cards.filter(card => card.kind !== 'Member'),
				quote: message.quote ? presentQuote(message.quote, myUserId) : null,
				// Until the server has them, my photos show from the browser.
				photos: message.photos.map(({ id, url, width, height }) => ({ id, url, width, height })),
			},
		})
	}

	const authors = new Map(stream.messages.map(message => [message.clientId, message.author]))
	return withAuthors(items, authors)
}

/**
 * Names others' runs of Messages: the author's name on the first, their avatar on the
 * last. A run is consecutive Messages from one author; a day separator or the divider ends it.
 */
const withAuthors = (items: ChatStreamItem[], authors: Map<string, ChatAuthor>): ChatStreamItem[] => {
	const othersAuthor = (index: number): ChatAuthor | undefined => {
		const item = items[index]
		if (item?.type !== 'message' || item.row.isMine) return undefined
		return authors.get(item.row.clientId)
	}
	return items.map((item, index) => {
		const author = othersAuthor(index)
		if (item.type !== 'message' || !author) return item
		return {
			...item,
			row: {
				...item.row,
				authorName: othersAuthor(index - 1)?.id !== author.id ? author.firstName : null,
				avatar: othersAuthor(index + 1)?.id !== author.id ? author : null,
			},
		}
	})
}
